'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { User, Edit3, Check, X, Crown, Heart, LogOut, ChevronRight, Star, Calendar, Shield, RotateCcw, Trash2, AlertTriangle, Bell, BellOff, Settings, Plus, BookOpen, Zap, RefreshCw, XCircle, Loader2, FileText, Download, Clock, CheckCircle2, AlertCircle, BellRing, Tag, Copy, ExternalLink, MessageSquare, Share2, Gift,  } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';
import UserInbox from './UserInbox';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import WinewoodPlusModal from '@/components/WinewoodPlusModal';
import PromoRedeemModal from './PromoRedeemModal';

/* ─── Avatar data ─── */
const AVATARS = [
  { key: 'avatar_1', src: '/assets/images/ChatGPT_Image_4._Sept._2026__13_52_38-1788522988030.png', label: 'Fuchs' },
  { key: 'avatar_2', src: '/assets/images/ChatGPT_Image_4._Sept._2026__13_52_44-1788522988017.png', label: 'Wolf' },
  { key: 'avatar_3', src: '/assets/images/ChatGPT_Image_4._Sept._2026__13_52_50-1788522987704.png', label: 'Bär' },
  { key: 'avatar_4', src: '/assets/images/ChatGPT_Image_4._Sept._2026__13_52_55-1788522987718.png', label: 'Otter' },
  { key: 'avatar_5', src: '/assets/images/ChatGPT_Image_4._Sept._2026__13_55_38-1788522974912.png', label: 'Waschbär' },
  { key: 'avatar_6', src: '/assets/images/ChatGPT_Image_4._Sept._2026__13_56_35-1788523049753.png', label: 'Katze' },
];

interface UserPreferences {
  location_radius_km: number;
  notifications_enabled: boolean;
  notifications_specials: boolean;
  notifications_waitlist: boolean;
  notifications_epic: boolean;
}

const DEFAULT_PREFS: UserPreferences = {
  location_radius_km: 50,
  notifications_enabled: true,
  notifications_specials: true,
  notifications_waitlist: true,
  notifications_epic: true,
};

interface StripeInvoice {
  id: string;
  number: string | null;
  amount_paid: number;
  amount_due: number;
  currency: string;
  status: string | null;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

interface UpcomingInvoice {
  amount_due: number;
  currency: string;
  period_start: number;
  period_end: number;
  next_payment_attempt: number | null;
}

interface SavedPromoCode {
  id: string;
  offer_id: string;
  promo_code: string;
  promo_code_text?: string | null;
  redeemed: boolean;
  redeemed_at: string | null;
  last_redeemed_at: string | null;
  saved_at: string;
  offer?: { title: string; city: string };
}

interface ReferralStats {
  totalReferrals: number;
  successfulReferrals: number;
  monthsEarned: number;
}

export default function ProfileClient() {
  const { user, signOut, loading, favoriteIds } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();
  const inboxRef = useRef<HTMLDivElement | null>(null);

  const [profile, setProfile] = useState<{
    id: string;
    display_name: string;
    email: string;
    avatar_key: string;
    role: 'user' | 'admin' | 'editor';
    manual_plus: boolean;
    is_plus: boolean;
    plus_expires_at: string | null;
    created_at: string;
  }>({
    id: '',
    display_name: '',
    email: '',
    avatar_key: 'avatar_1',
    role: 'user',
    manual_plus: false,
    is_plus: false,
    plus_expires_at: null,
    created_at: new Date().toISOString(),
  });
  const [subscription, setSubscription] = useState<{
    id?: string;
    stripe_subscription_id?: string;
    stripe_customer_id?: string;
    status?: string;
    plan?: string;
    current_period_end?: string;
    expires_at?: string;
    cancel_at_period_end?: boolean;
  } | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFS);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [recentFavorites, setRecentFavorites] = useState<any[]>([]);
  const [showPlusModal, setShowPlusModal] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  // Invoices
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState<UpcomingInvoice | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showAllInvoices, setShowAllInvoices] = useState(false);

  // Push notifications
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Saved promo codes
  const [savedPromoCodes, setSavedPromoCodes] = useState<SavedPromoCode[]>([]);
  const [promoCodesLoading, setPromoCodesLoading] = useState(false);
  const [redeemingPromo, setRedeemingPromo] = useState<SavedPromoCode | null>(null);

  // Unread message count for header badge
  const [unreadCount, setUnreadCount] = useState(0);

  // Referral stats
  const [referralStats, setReferralStats] = useState<ReferralStats>({ totalReferrals: 0, successfulReferrals: 0, monthsEarned: 0 });
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralLinkCopied, setReferralLinkCopied] = useState(false);

  // Prevent the checkout activation effect from firing more than once
  const checkoutHandledRef = useRef(false);

  // Handle Stripe return redirect
  useEffect(() => {
    const checkout = searchParams?.get('checkout');
    const sessionId = searchParams?.get('session_id');
    const inquiry = searchParams?.get('inquiry');

    // Wait until auth is resolved before acting on checkout=success
    if (checkout === 'success') {
      // If auth is still loading, wait for the next render (user will be set)
      if (loading) return;
      // If user is not logged in after loading completes, nothing we can do
      if (!user?.id) return;
      // Prevent running twice (e.g. when searchParams and user both trigger the effect)
      if (checkoutHandledRef.current) return;
      checkoutHandledRef.current = true;

      // Step 1: If we have a session_id, call verify-session endpoint immediately.
      // This writes the subscription to DB via service role (bypasses RLS + webhook dependency).
      // Step 2: Then poll DB to confirm the row is readable by the user.
      let attempts = 0;
      const maxAttempts = 8;
      const pollInterval = 2000;

      const activateViaSessionId = async (): Promise<boolean> => {
        if (!sessionId || !user?.id) return false;
        try {
          // Get the current session token to authenticate the request server-side
          const { data: { session: supabaseSession } } = await supabase.auth.getSession();
          const accessToken = supabaseSession?.access_token;

          if (!accessToken) return false;

          const res = await fetch('/api/stripe/verify-session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ sessionId }),
          });
          if (res.ok) {
            const data = await res.json();
            if (data.success && data.subscription) {
              setSubscription(data.subscription);
              setProfile((prev) => ({
                ...prev,
                is_plus: true,
                plus_expires_at: data.subscription.expires_at || data.subscription.current_period_end || null,
              }));
              toast.success('WINEWOOD+ erfolgreich aktiviert! 🎉', {
                description: 'Dein Abonnement ist jetzt aktiv.',
                duration: 5000,
              });
              router.replace('/profile');
              return true;
            }
          }
        } catch {
          // fall through to polling
        }
        return false;
      };

      const pollSubscription = async () => {
        attempts++;
        try {
          const { data: subData } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', user?.id ?? '')
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (subData) {
            // Subscription found — update state and show success toast
            setSubscription(subData);
            setProfile((prev) => ({
              ...prev,
              is_plus: true,
              plus_expires_at: subData.expires_at || subData.current_period_end || null,
            }));
            toast.success('WINEWOOD+ erfolgreich aktiviert! 🎉', {
              description: 'Dein Abonnement ist jetzt aktiv.',
              duration: 5000,
            });
            router.replace('/profile');
            return;
          }
        } catch {
          // ignore fetch errors during polling
        }

        if (attempts < maxAttempts) {
          setTimeout(pollSubscription, pollInterval);
        } else {
          // All attempts exhausted — reload profile to pick up any late webhook write
          toast.info('WINEWOOD+ wird aktiviert…', {
            description: 'Bitte Seite neu laden, falls W+ nicht erscheint.',
            duration: 8000,
          });
          router.replace('/profile');
        }
      };

      // Try direct session verification first (fastest path), then fall back to polling
      const startActivation = async () => {
        if (sessionId && user?.id) {
          const activated = await activateViaSessionId();
          if (activated) return;
        }
        // Fall back to polling the DB (webhook path)
        setTimeout(pollSubscription, 1000);
      };

      setTimeout(startActivation, 800);
    } else if (checkout === 'cancelled') {
      toast.info('Zahlung abgebrochen', { description: 'Du kannst jederzeit upgraden.', duration: 3000 });
      router.replace('/profile');
    }
    if (inquiry) {
      setInquiryMessage(decodeURIComponent(inquiry));
      setShowInquiryModal(true);
      router.replace('/profile');
    }
  }, [searchParams, user, loading]);

  useEffect(() => {
    if (!user) return;

    const loadProfileData = async () => {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileData) {
        const { data: subData } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        setSubscription(subData);

        const isPlus = profileData.manual_plus ||
          (subData && (!subData.expires_at || new Date(subData.expires_at) > new Date()));
        const plusExpiresAt = profileData.manual_plus_until || subData?.expires_at || subData?.current_period_end || null;

        setProfile({
          id: profileData.id,
          display_name: profileData.display_name || user.email?.split('@')[0] || 'Nutzer',
          email: user.email || '',
          avatar_key: profileData.avatar_key || 'avatar_1',
          role: profileData.role || 'user',
          manual_plus: profileData.manual_plus,
          is_plus: isPlus,
          plus_expires_at: plusExpiresAt,
          created_at: profileData.created_at || user.created_at || new Date().toISOString(),
        });
        setNameInput(profileData.display_name || user.email?.split('@')[0] || 'Nutzer');
      } else {
        setProfile((p) => ({
          ...p,
          id: user.id,
          display_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Nutzer',
          email: user.email || '',
          created_at: user.created_at || new Date().toISOString(),
        }));
        setNameInput(user.user_metadata?.full_name || user.email?.split('@')[0] || 'Nutzer');
      }

      const { data: prefsData } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (prefsData) {
        setPreferences({
          location_radius_km: prefsData.location_radius_km,
          notifications_enabled: prefsData.notifications_enabled,
          notifications_specials: prefsData.notifications_specials,
          notifications_waitlist: prefsData.notifications_waitlist,
          notifications_epic: prefsData.notifications_epic,
        });
      }

      const { data: favData } = await supabase
        .from('favorites')
        .select(`offer_id, offers ( id, title, city, image_url, is_epic_location, is_special, price, discount_amount )`)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(3);

      if (favData) {
        setFavoritesCount(favData.length);
        setRecentFavorites(favData.map((f: any) => f.offers).filter(Boolean));
      }
    };

    loadProfileData();
  }, [user]);

  // Load saved promo codes
  useEffect(() => {
    if (!user) return;
    const loadPromoCodes = async () => {
      setPromoCodesLoading(true);
      try {
        const { data } = await supabase
          .from('saved_promo_codes')
          .select('id, offer_id, promo_code, promo_code_text, redeemed, redeemed_at, last_redeemed_at, saved_at, offer:offers(title, city)')
          .eq('user_id', user.id)
          .order('saved_at', { ascending: false });
        setSavedPromoCodes((data || []).map((d: any) => ({
          ...d,
          offer: Array.isArray(d.offer) ? d.offer[0] : d.offer,
        })));
      } catch {
        // silently fail
      } finally {
        setPromoCodesLoading(false);
      }
    };
    loadPromoCodes();
  }, [user]);

  useEffect(() => {
    if (favoriteIds) {
      setFavoritesCount(favoriteIds.size);
    }
  }, [favoriteIds]);

  // Load referral stats
  useEffect(() => {
    if (!user) return;
    const loadReferralStats = async () => {
      setReferralLoading(true);
      try {
        const { data, count } = await supabase
          .from('referrals')
          .select('reward_granted', { count: 'exact' })
          .eq('referrer_id', user.id);

        const successful = (data || []).filter((r: any) => r.reward_granted).length;
        setReferralStats({
          totalReferrals: count || 0,
          successfulReferrals: successful,
          monthsEarned: successful,
        });
      } catch {
        // silently fail
      } finally {
        setReferralLoading(false);
      }
    };
    loadReferralStats();
  }, [user]);

  // Load invoices when subscription is available
  useEffect(() => {
    if (!user || !subscription?.stripe_customer_id) return;
    const loadInvoices = async () => {
      setInvoicesLoading(true);
      try {
        const res = await fetch(`/api/stripe/invoices?userId=${user.id}`);
        const data = await res.json();
        if (data.invoices) setInvoices(data.invoices);
        if (data.upcoming) setUpcomingInvoice(data.upcoming);
      } catch {
        // silently fail
      } finally {
        setInvoicesLoading(false);
      }
    };
    loadInvoices();
  }, [user, subscription?.stripe_customer_id]);

  // Check push subscription state
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    setPushSupported(true);

    const checkSubscription = async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushSubscribed(!!sub);
      } catch {
        // ignore
      }
    };
    checkSubscription();
  }, []);

  // Register service worker on mount
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  }, []);

  // Load unread message count for header badge
  useEffect(() => {
    if (!user) return;
    const loadUnread = async () => {
      const { count } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .eq('sender_id', user.id)
        .eq('read_by_recipient', false);
      setUnreadCount(count || 0);
    };
    loadUnread();

    // Realtime: update badge when messages change
    const channel = supabase
      .channel(`profile-unread-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages', filter: `sender_id=eq.${user.id}` }, () => {
        loadUnread();
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'message_replies' }, () => {
        loadUnread();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const handleScrollToInbox = () => {
    if (!inboxRef.current) return;
    inboxRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Deep-link from push notification: scroll to inbox when ?tab=messages is present
  useEffect(() => {
    const tabParam = searchParams?.get('tab');
    if (tabParam !== 'messages') return;

    // Use a robust scroll approach: try getElementById with retries
    let attempts = 0;
    const maxAttempts = 12;

    const tryScroll = () => {
      attempts++;
      const el = document.getElementById('inbox-section') || inboxRef.current;
      if (el) {
        // Use instant scroll to avoid being overridden by other scroll events
        el.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
        // Also set window.scrollY explicitly as a fallback
        const rect = el.getBoundingClientRect();
        const scrollTop = window.scrollY + rect.top - 80; // 80px offset for sticky header
        window.scrollTo({ top: scrollTop, behavior: 'instant' as ScrollBehavior });
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(tryScroll, 250);
      }
    };

    // Start after a delay to let the page fully render and data load
    const timer = setTimeout(tryScroll, 800);
    return () => clearTimeout(timer);
  }, [searchParams]);

  const selectedAvatar = AVATARS.find((a) => a.key === profile.avatar_key) ?? AVATARS[0];

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) { toast.error('Name darf nicht leer sein'); return; }
    try {
      await supabase.from('profiles').update({ display_name: trimmed }).eq('id', user.id);
      setProfile((p) => ({ ...p, display_name: trimmed }));
      setEditingName(false);
      toast.success('Name gespeichert', { icon: '✅' });
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleCancelName = () => {
    setNameInput(profile.display_name);
    setEditingName(false);
  };

  const handleSelectAvatar = async (key: string) => {
    try {
      await supabase.from('profiles').update({ avatar_key: key }).eq('id', user.id);
      setProfile((p) => ({ ...p, avatar_key: key }));
      setShowAvatarPicker(false);
      toast.success('Avatar aktualisiert', { icon: '🎨' });
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  const savePreferences = async (updated: UserPreferences) => {
    if (!user) return;
    setPrefsSaving(true);
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert({ user_id: user.id, ...updated }, { onConflict: 'user_id' });
      if (error) throw error;
      toast.success('Einstellungen gespeichert', { icon: '⚙️', duration: 1500 });
    } catch {
      toast.error('Fehler beim Speichern der Einstellungen');
    } finally {
      setPrefsSaving(false);
    }
  };

  const updatePref = <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    savePreferences(updated);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('Abgemeldet', { icon: '👋' });
      router.push('/sign-up-login-screen');
    } catch {
      toast.error('Fehler beim Abmelden');
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription?.stripe_subscription_id || !user) return;
    setCancellingSubscription(true);
    try {
      const res = await fetch('/api/stripe/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId: subscription.stripe_subscription_id,
          userId: user.id,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubscription((s) => s ? { ...s, cancel_at_period_end: true } : s);
        toast.success('Abo wird zum Ende der Laufzeit gekündigt', {
          description: data.cancel_at ? `Aktiv bis ${new Date(data.cancel_at).toLocaleDateString('de-DE')}` : undefined,
          icon: '✅',
          duration: 4000,
        });
        setShowCancelConfirm(false);
      } else {
        throw new Error(data.error);
      }
    } catch (err: any) {
      toast.error('Kündigung fehlgeschlagen', { description: err.message });
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'LÖSCHEN') {
      toast.error('Bitte gib "LÖSCHEN" ein um zu bestätigen');
      return;
    }
    setIsDeleting(true);
    try {
      if (user?.id) {
        await supabase.from('profiles').delete().eq('id', user.id);
      }
      await signOut();
      toast.success('Konto gelöscht. Alle Daten wurden entfernt.', { icon: '🗑️' });
      router.push('/sign-up-login-screen');
    } catch {
      toast.error('Fehler beim Löschen des Kontos. Bitte kontaktiere den Support.');
      setIsDeleting(false);
    }
  };

  const handleTogglePush = async () => {
    if (!user || !pushSupported) return;
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (pushSubscribed) {
        // Unsubscribe
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await fetch('/api/push/subscribe', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ endpoint: sub.endpoint }),
          });
          await sub.unsubscribe();
        }
        setPushSubscribed(false);
        toast.success('Push-Benachrichtigungen deaktiviert', { icon: '🔕' });
      } else {
        // Request permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error('Benachrichtigungen wurden blockiert. Bitte in den Browser-Einstellungen erlauben.');
          return;
        }
        const vapidKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
        if (!vapidKey || vapidKey.includes('your-')) {
          toast.error('Push-Konfiguration fehlt. Bitte NEXT_PUBLIC_VAPID_PUBLIC_KEY setzen.');
          return;
        }
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        await fetch('/api/push/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id, subscription: sub.toJSON() }),
        });
        setPushSubscribed(true);
        toast.success('Push-Benachrichtigungen aktiviert! 🔔', {
          description: 'Du erhältst Benachrichtigungen für Nachrichten, Wartelisten & Neuigkeiten.',
          duration: 4000,
        });
      }
    } catch (err: any) {
      toast.error('Fehler bei Push-Benachrichtigungen', { description: err.message });
    } finally {
      setPushLoading(false);
    }
  };

  const handleOpenRedeemModal = (promoCode: SavedPromoCode) => {
    setRedeemingPromo(promoCode);
  };

  const handlePartnerConfirm = async (id: string) => {
    const pc = savedPromoCodes.find((p) => p.id === id);
    if (!pc || !user) throw new Error('Not found');

    const now = new Date().toISOString();

    // 1. Mark promo code as redeemed + update last_redeemed_at
    const { error: redeemError } = await supabase
      .from('saved_promo_codes')
      .update({ redeemed: true, redeemed_at: now, last_redeemed_at: now })
      .eq('id', id);
    if (redeemError) throw redeemError;

    // 2. Create a booking entry for the redeemed promo
    const bookingRef = `PROMO-${pc.promo_code}-${Date.now().toString(36).toUpperCase()}`;
    await supabase.from('user_bookings').insert({
      user_id: user.id,
      offer_id: pc.offer_id || null,
      booking_ref: bookingRef,
      offer_title: pc.offer?.title || 'Promo-Code Einlösung',
      offer_city: pc.offer?.city || '',
      booking_type: 'promo',
      status: 'completed',
      notes: `Promo-Code: ${pc.promo_code}`,
    });

    // 3. Update local state
    setSavedPromoCodes((prev) =>
      prev.map((p) => p.id === id ? { ...p, redeemed: true, redeemed_at: now, last_redeemed_at: now } : p)
    );
    toast.success('Promo-Code eingelöst & in Buchungen gespeichert 🎉', { duration: 3500 });
  };

  const handleDeletePromoCode = async (id: string) => {
    try {
      await supabase.from('saved_promo_codes').delete().eq('id', id);
      setSavedPromoCodes((prev) => prev.filter((p) => p.id !== id));
      toast.success('Promo-Code entfernt');
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleCopyReferralLink = async () => {
    if (!user) return;
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
    const link = `${siteUrl}/join?ref=${user.id}`;
    try {
      await navigator.clipboard.writeText(link);
      setReferralLinkCopied(true);
      toast.success('Einladungslink kopiert! 🎉', { description: 'Teile ihn mit Freunden und erhalte 1 Monat W+', duration: 3000 });
      setTimeout(() => setReferralLinkCopied(false), 2500);
    } catch {
      toast.error('Link konnte nicht kopiert werden');
    }
  };

  const handleShareReferralLink = async () => {
    if (!user) return;
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
    const link = `${siteUrl}/join?ref=${user.id}`;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: 'WINEWOOD Adventure',
          text: 'Entdecke einzigartige Weinerlebnisse mit WINEWOOD Adventure! Melde dich über meinen Link an:',
          url: link,
        });
        return;
      } catch (err: any) {
        // AbortError = user cancelled the share sheet — do nothing
        if (err?.name === 'AbortError') return;
        // Any other error (NotAllowedError, etc.) — fall back to copy
      }
    }
    // Fallback: copy to clipboard
    handleCopyReferralLink();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!user) return <NotLoggedIn />;

  const planLabel = subscription?.plan === 'yearly' ? 'Jährlich (39,90 €/Jahr)' : subscription?.plan === 'monthly' ? 'Monatlich (3,99 €/Monat)' : 'WINEWOOD+';
  const renewalDate = subscription?.current_period_end || subscription?.expires_at || profile.plus_expires_at;
  // True when W+ comes only from a manual/referral grant (no active Stripe subscription)
  const isManualPlusOnly = profile.manual_plus && !subscription?.stripe_subscription_id;

  const displayedInvoices = showAllInvoices ? invoices : invoices.slice(0, 3);

  return (
    <div className="min-h-screen pb-24" style={{ background: 'var(--background)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-40 px-4 flex items-center justify-between"
        style={{
          background: 'rgba(27,29,35,0.96)',
          borderBottom: '1px solid var(--border)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          paddingBottom: '12px',
        }}>
        <div className="flex items-center gap-2">
          <Image
            src="/assets/images/logo_grun_fur_app-1787922846254.png"
            alt="WINEWOOD Adventure"
            width={110}
            height={44}
            className="object-contain"
            priority
          />
        </div>
        {/* Chat/Bell icon with unread badge */}
        <button
          onClick={handleScrollToInbox}
          className="relative flex items-center justify-center w-10 h-10 rounded-full transition-all duration-150 active:scale-95"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
          aria-label="Nachrichten öffnen"
        >
          <MessageSquare size={18} color={unreadCount > 0 ? 'var(--primary)' : 'var(--muted-foreground)'} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold px-1"
              style={{ background: '#ef4444', color: '#fff', lineHeight: 1 }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      <div className="px-4 pt-5 max-w-mobile mx-auto space-y-4">
        {/* Profile Card */}
        <div className="rounded-app p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <button
              onClick={() => setShowAvatarPicker(true)}
              className="relative flex-shrink-0 group"
              aria-label="Avatar ändern">
              <div
                className="w-14 h-14 rounded-full overflow-hidden transition-transform duration-150 group-active:scale-95"
                style={{ background: 'var(--muted)', border: '2px solid var(--border)' }}>
                <Image
                  src={selectedAvatar.src}
                  alt={selectedAvatar.label}
                  width={56}
                  height={56}
                  className="w-full h-full object-cover"
                />
              </div>
              <div
                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: 'var(--primary)' }}>
                <Edit3 size={10} color="#fff" />
              </div>
            </button>

            {/* Name + email */}
            <div className="flex-1 min-w-0">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') handleCancelName();
                    }}
                    className="flex-1 min-w-0 text-sm font-semibold bg-transparent border-b outline-none"
                    style={{ color: 'var(--card-foreground)', borderColor: 'var(--primary)' }}
                    autoFocus
                    maxLength={40}
                  />
                  <button onClick={handleSaveName} className="p-1 rounded flex-shrink-0" style={{ color: 'var(--primary)' }}>
                    <Check size={16} />
                  </button>
                  <button onClick={handleCancelName} className="p-1 rounded flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold truncate" style={{ color: 'var(--card-foreground)' }}>
                    {profile.display_name}
                  </span>
                  <button
                    onClick={() => { setEditingName(true); setNameInput(profile.display_name); }}
                    className="p-1 rounded flex-shrink-0"
                    style={{ color: 'var(--muted-foreground)' }}
                    aria-label="Name bearbeiten">
                    <Edit3 size={12} />
                  </button>
                </div>
              )}
              <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--muted-foreground)' }}>
                {profile.email}
              </p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-pill"
                  style={{
                    background: profile.role === 'admin' ? 'rgba(239,68,68,0.15)' : profile.role === 'editor' ? 'rgba(234,179,8,0.15)' : 'rgba(124,179,66,0.15)',
                    color: profile.role === 'admin' ? 'var(--error)' : profile.role === 'editor' ? 'var(--gold)' : 'var(--primary)',
                  }}>
                  {profile.role === 'admin' ? 'Admin' : profile.role === 'editor' ? 'Editor' : 'Mitglied'}
                </span>
                <div className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                  <Calendar size={10} />
                  <span>seit {new Date(profile.created_at).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* WINEWOOD+ Subscription Status */}
        <div
          className="rounded-app overflow-hidden"
          style={{
            background: profile.is_plus
              ? 'linear-gradient(135deg, rgba(234,179,8,0.12) 0%, rgba(234,179,8,0.04) 100%)'
              : 'var(--card)',
            border: profile.is_plus ? '1px solid rgba(234,179,8,0.3)' : '1px solid var(--border)',
          }}>
          {/* Header row */}
          <div className="flex items-center justify-between gap-3 p-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: profile.is_plus ? 'rgba(234,179,8,0.2)' : 'var(--muted)' }}>
                <Crown size={17} color={profile.is_plus ? 'var(--gold)' : 'var(--muted-foreground)'} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold" style={{ color: profile.is_plus ? 'var(--gold)' : 'var(--card-foreground)' }}>
                  WINEWOOD+
                </p>
                {profile.is_plus ? (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                    {isManualPlusOnly ? 'Bonus-Monat aktiv' : subscription?.cancel_at_period_end ? 'Wird gekündigt' : 'Aktives Abonnement'}
                  </p>
                ) : (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                    Exklusive Rabatte &amp; Epic-Inhalte
                  </p>
                )}
              </div>
            </div>
            {profile.is_plus ? (
              <span
                className="text-[10px] font-bold px-2.5 py-1 rounded-pill flex-shrink-0"
                style={{
                  background: subscription?.cancel_at_period_end ? 'rgba(239,68,68,0.15)' : 'rgba(234,179,8,0.2)',
                  color: subscription?.cancel_at_period_end ? 'var(--error)' : 'var(--gold)',
                }}>
                {subscription?.cancel_at_period_end ? 'ENDET BALD' : 'AKTIV'}
              </span>
            ) : (
              <button
                className="flex items-center gap-1 px-3 py-1.5 rounded-pill text-xs font-semibold transition-all duration-150 active:scale-95 flex-shrink-0"
                style={{ background: 'var(--gold)', color: '#000' }}
                onClick={() => setShowPlusModal(true)}>
                <Zap size={12} />
                Upgrade
              </button>
            )}
          </div>

          {/* Active subscription details */}
          {profile.is_plus && (
            <div style={{ borderTop: '1px solid rgba(234,179,8,0.15)' }}>
              <div className="px-4 py-3 space-y-2">
                {/* Plan */}
                {subscription?.plan && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Plan</span>
                    <span className="text-xs font-semibold" style={{ color: 'var(--card-foreground)' }}>{planLabel}</span>
                  </div>
                )}
                {/* Renewal / expiry date */}
                {renewalDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {isManualPlusOnly ? 'Gültig bis' : subscription?.cancel_at_period_end ? 'Endet am' : 'Verlängerung am'}
                    </span>
                    <div className="flex items-center gap-1">
                      {!subscription?.cancel_at_period_end && !isManualPlusOnly && <RefreshCw size={10} color="var(--muted-foreground)" />}
                      <span className="text-xs font-semibold" style={{ color: subscription?.cancel_at_period_end ? 'var(--error)' : 'var(--card-foreground)' }}>
                        {new Date(renewalDate).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Cancel button — only if Stripe subscription and not already cancelling */}
              {subscription?.stripe_subscription_id && !subscription?.cancel_at_period_end && (
                <div className="px-4 pb-3">
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all duration-150 active:scale-95"
                    style={{
                      background: 'rgba(239,68,68,0.08)',
                      border: '1px solid rgba(239,68,68,0.2)',
                      color: 'var(--error)',
                    }}>
                    <XCircle size={13} />
                    Abo kündigen
                  </button>
                </div>
              )}

              {subscription?.cancel_at_period_end && (
                <div className="px-4 pb-3">
                  <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
                    Dein Abo läuft bis zum Ende der Laufzeit weiter.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Benefits for non-plus users */}
          {!profile.is_plus && (
            <div className="px-4 pb-4 pt-1">
              <div className="space-y-1.5 mb-3">
                {[
                  'Exklusive WINEWOOD+ Rabatte',
                  'Wegbeschreibungen zu EpicLocations',
                  'Mitglieder only Erlebnisse',
                  'Zugang zu WINEWOOD+ Angeboten',
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(163,196,74,0.15)' }}>
                      <Check size={9} color="#a3c44a" />
                    </div>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>{feature}</span>
                  </div>
                ))}
              </div>
              <button
                className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-full text-xs font-bold transition-all duration-150 active:scale-[0.98]"
                style={{ background: '#a3c44a', color: '#1a1c22' }}
                onClick={() => setShowPlusModal(true)}
              >
                <Zap size={13} fill="#1a1c22" />
                Jetzt WINEWOOD+ holen
              </button>
            </div>
          )}
        </div>

        {/* My Bookings link */}
        <Link
          href="/bookings"
          className="flex items-center gap-3 px-4 py-3.5 rounded-app transition-all duration-150 active:scale-[0.99]"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        >
          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(124,179,66,0.12)' }}>
            <BookOpen size={16} color="var(--primary)" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: 'var(--card-foreground)' }}>Meine Buchungen</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Vergangene &amp; bevorstehende Buchungen</p>
          </div>
          <ChevronRight size={14} color="var(--muted-foreground)" />
        </Link>

        {/* Messages Inbox */}
        <div ref={inboxRef} id="inbox-section">
          <UserInbox />
        </div>

        {/* ── Referral Section ── */}
        <div
          className="rounded-app overflow-hidden"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
            <Gift size={14} color="var(--primary)" />
            <h2 className="text-sm font-semibold flex-1" style={{ color: 'var(--card-foreground)' }}>Freunde einladen</h2>
            {referralLoading && <Loader2 size={13} className="animate-spin" color="var(--muted-foreground)" />}
          </div>

          <div className="px-4 py-4 space-y-4">
            {/* Description */}
            <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Lade Freunde ein und erhalte für jede erfolgreiche Registrierung <strong style={{ color: 'var(--primary)' }}>1 Monat WINEWOOD+</strong> gratis — bis zu <strong style={{ color: 'var(--primary)' }}>3 Monate</strong> insgesamt.
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2">
              <div
                className="rounded-app-sm p-2.5 text-center"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
              >
                <p className="text-lg font-bold" style={{ color: 'var(--card-foreground)' }}>
                  {referralStats.totalReferrals}
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Eingeladen</p>
              </div>
              <div
                className="rounded-app-sm p-2.5 text-center"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
              >
                <p className="text-lg font-bold" style={{ color: 'var(--primary)' }}>
                  {referralStats.successfulReferrals}
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Erfolgreich</p>
              </div>
              <div
                className="rounded-app-sm p-2.5 text-center"
                style={{
                  background: referralStats.monthsEarned > 0 ? 'rgba(234,179,8,0.1)' : 'var(--muted)',
                  border: `1px solid ${referralStats.monthsEarned > 0 ? 'rgba(234,179,8,0.3)' : 'var(--border)'}`,
                }}
              >
                <p className="text-lg font-bold" style={{ color: referralStats.monthsEarned > 0 ? 'var(--gold)' : 'var(--card-foreground)' }}>
                  {referralStats.monthsEarned}
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Monate W+</p>
              </div>
            </div>

            {/* Limit indicator */}
            {referralStats.successfulReferrals >= 3 ? (
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-app-sm"
                style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.25)' }}
              >
                <Crown size={13} color="var(--gold)" />
                <p className="text-xs font-medium" style={{ color: 'var(--gold)' }}>
                  Maximum erreicht — 3/3 Empfehlungen eingelöst 🎉
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex-1 h-1.5 rounded-pill transition-all duration-300"
                    style={{
                      background: i < referralStats.successfulReferrals ? 'var(--primary)' : 'var(--border)',
                    }}
                  />
                ))}
                <span className="text-[10px] ml-1 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>
                  {referralStats.successfulReferrals}/3
                </span>
              </div>
            )}

            {/* Share buttons */}
            {referralStats.successfulReferrals < 3 && (
              <div className="flex gap-2">
                <button
                  onClick={handleShareReferralLink}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-pill text-xs font-bold transition-all duration-150 active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #7CB342 0%, #9CCC65 100%)', color: '#fff' }}
                >
                  <Share2 size={13} />
                  Einladen
                </button>
                <button
                  onClick={handleCopyReferralLink}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-pill text-xs font-semibold transition-all duration-150 active:scale-[0.98]"
                  style={{
                    background: referralLinkCopied ? 'rgba(124,179,66,0.15)' : 'var(--muted)',
                    border: `1px solid ${referralLinkCopied ? 'rgba(124,179,66,0.4)' : 'var(--border)'}`,
                    color: referralLinkCopied ? 'var(--primary)' : 'var(--muted-foreground)',
                  }}
                >
                  {referralLinkCopied ? <Check size={13} /> : <Copy size={13} />}
                  {referralLinkCopied ? 'Kopiert!' : 'Link kopieren'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Favorites Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Heart size={15} color="var(--error)" fill="var(--error)" />
              <h2 className="text-sm font-semibold" style={{ color: 'var(--card-foreground)' }}>Meine Favoriten</h2>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-pill" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                {favoritesCount}
              </span>
            </div>
            <Link href="/favorites" className="text-xs font-medium" style={{ color: 'var(--primary)' }}>
              Alle →
            </Link>
          </div>

          {recentFavorites.length === 0 ? (
            <div className="rounded-app p-4 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Noch keine Favoriten gespeichert.</p>
              <Link href="/" className="text-xs font-semibold mt-1 inline-block" style={{ color: 'var(--primary)' }}>
                Angebote entdecken →
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recentFavorites.map((fav) => (
                <Link
                  key={fav.id}
                  href={`/offer-detail-page?id=${fav.id}`}
                  className="flex items-center gap-3 p-3 rounded-app transition-all duration-150 active:scale-[0.99]"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                  <div className="w-12 h-12 rounded-app-sm overflow-hidden flex-shrink-0 relative">
                    {fav.image_url ? (
                      <img src={fav.image_url} alt={fav.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--muted)' }}>
                        <Heart size={16} color="var(--muted-foreground)" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--card-foreground)' }}>{fav.title}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                      {fav.city}
                      {fav.is_epic_location && <span className="ml-2 text-[10px] font-bold" style={{ color: 'var(--accent)' }}>EPIC</span>}
                      {fav.is_special && <span className="ml-2 text-[10px] font-bold" style={{ color: 'var(--primary)' }}>SPEZIAL</span>}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {fav.price === 0 ? (
                      <span className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>Kostenlos</span>
                    ) : (
                      <span className="text-xs font-semibold" style={{ color: 'var(--card-foreground)' }}>
                        {fav.discount_amount > 0 ? `${(fav.price - fav.discount_amount).toFixed(2)} €` : `${fav.price.toFixed(2)} €`}
                      </span>
                    )}
                  </div>
                  <ChevronRight size={14} color="var(--muted-foreground)" />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Preferences — compact, secondary feel */}
        <div className="rounded-app overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
            <Settings size={12} color="var(--muted-foreground)" />
            <h2 className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Einstellungen</h2>
            {prefsSaving && <div className="w-3 h-3 rounded-full border border-t-transparent animate-spin ml-auto" style={{ borderColor: 'var(--muted-foreground)', borderTopColor: 'transparent' }} />}
          </div>

          {/* Push notifications toggle */}
          {pushSupported && (
            <div className="px-3 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BellRing size={12} color={pushSubscribed ? 'var(--primary)' : 'var(--muted-foreground)'} />
                  <div>
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Push-Benachrichtigungen</span>
                    <p className="text-[10px]" style={{ color: 'var(--muted-foreground)', opacity: 0.7 }}>
                      Nachrichten, Wartelisten, Neuigkeiten
                    </p>
                  </div>
                </div>
                {pushLoading ? (
                  <Loader2 size={16} className="animate-spin" color="var(--muted-foreground)" />
                ) : (
                  <ToggleSwitch value={pushSubscribed} onChange={handleTogglePush} />
                )}
              </div>
            </div>
          )}

          {/* Notifications master toggle */}
          <div className="px-3 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {preferences.notifications_enabled ? <Bell size={12} color="var(--muted-foreground)" /> : <BellOff size={12} color="var(--muted-foreground)" />}
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>In-App Benachrichtigungen</span>
              </div>
              <ToggleSwitch
                value={preferences.notifications_enabled}
                onChange={(v) => updatePref('notifications_enabled', v)}
              />
            </div>
          </div>

          {/* Notification sub-settings */}
          {preferences.notifications_enabled && (
            <>
              {[
                { key: 'notifications_specials' as const, label: 'Spezial-Angebote', icon: '⭐' },
                { key: 'notifications_waitlist' as const, label: 'Wartelisten-Updates', icon: '📋' },
                { key: 'notifications_epic' as const, label: 'Epic Locations', icon: '🗺️' },
              ].map((item, idx, arr) => (
                <div
                  key={item.key}
                  className="px-3 py-2 flex items-center justify-between"
                  style={{ borderBottom: idx < arr.length - 1 ? '1px solid var(--border)' : 'none', paddingLeft: '2rem' }}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">{item.icon}</span>
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
                  </div>
                  <ToggleSwitch
                    value={preferences[item.key]}
                    onChange={(v) => updatePref(item.key, v)}
                  />
                </div>
              ))}
            </>
          )}
        </div>

        {/* ── Invoices Section (only for Plus users with Stripe subscription) ── */}
        {profile.is_plus && subscription?.stripe_customer_id && (
          <div className="rounded-app overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            {/* Section header */}
            <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
              <FileText size={14} color="var(--gold)" />
              <h2 className="text-sm font-semibold flex-1" style={{ color: 'var(--card-foreground)' }}>Rechnungen</h2>
              {invoicesLoading && <Loader2 size={13} className="animate-spin" color="var(--muted-foreground)" />}
            </div>

            {/* Upcoming invoice */}
            {upcomingInvoice && !subscription?.cancel_at_period_end && (
              <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border)', background: 'rgba(234,179,8,0.04)' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock size={13} color="var(--gold)" />
                    <div>
                      <p className="text-xs font-semibold" style={{ color: 'var(--card-foreground)' }}>Nächste Zahlung</p>
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                        {upcomingInvoice.next_payment_attempt
                          ? new Date(upcomingInvoice.next_payment_attempt * 1000).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
                          : 'Demnächst'}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-bold" style={{ color: 'var(--gold)' }}>
                    {(upcomingInvoice.amount_due / 100).toFixed(2)} {upcomingInvoice.currency.toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            {/* Past invoices */}
            {invoicesLoading ? (
              <div className="px-4 py-6 flex items-center justify-center">
                <Loader2 size={20} className="animate-spin" color="var(--muted-foreground)" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="px-4 py-5 text-center">
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Noch keine Rechnungen vorhanden.</p>
              </div>
            ) : (
              <>
                {displayedInvoices.map((inv, idx) => (
                  <InvoiceRow key={inv.id} invoice={inv} isLast={idx === displayedInvoices.length - 1 && (!showAllInvoices || invoices.length <= 3)} />
                ))}
                {invoices.length > 3 && (
                  <button
                    onClick={() => setShowAllInvoices((v) => !v)}
                    className="w-full py-2.5 text-xs font-medium transition-all duration-150"
                    style={{ color: 'var(--primary)', borderTop: '1px solid var(--border)' }}>
                    {showAllInvoices ? 'Weniger anzeigen ↑' : `Alle ${invoices.length} Rechnungen anzeigen ↓`}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Saved Promo Codes */}
        {(savedPromoCodes.length > 0 || promoCodesLoading) && (
          <div className="rounded-app overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
              <Tag size={14} color="var(--primary)" />
              <h2 className="text-sm font-semibold flex-1" style={{ color: 'var(--card-foreground)' }}>Gespeicherte Promocodes</h2>
              {promoCodesLoading && <Loader2 size={13} className="animate-spin" color="var(--muted-foreground)" />}
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-pill" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                {savedPromoCodes.length}
              </span>
            </div>

            {promoCodesLoading ? (
              <div className="px-4 py-6 flex items-center justify-center">
                <Loader2 size={20} className="animate-spin" color="var(--muted-foreground)" />
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                {savedPromoCodes.map((pc) => (
                  <PromoCodeRow
                    key={pc.id}
                    promoCode={pc}
                    onOpenRedeem={handleOpenRedeemModal}
                    onDelete={handleDeletePromoCode}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin link */}
        {(profile.role === 'admin' || profile.role === 'editor') && (
          <Link
            href="/admin"
            className="flex items-center gap-3 px-4 py-3.5 rounded-app transition-all duration-150 active:scale-[0.99]"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: 'var(--card-foreground)' }}>
            <Shield size={15} color="var(--error)" />
            <span className="flex-1 text-sm font-semibold" style={{ color: 'var(--error)' }}>Admin Panel</span>
            <ChevronRight size={14} color="var(--error)" />
          </Link>
        )}

        {/* Logout */}
        <button
          className="w-full py-3.5 rounded-app text-sm font-semibold transition-all duration-150 active:scale-[0.99] flex items-center justify-center gap-2"
          style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--error)', border: '1px solid rgba(239,68,68,0.2)' }}
          onClick={handleLogout}>
          <LogOut size={15} />
          Abmelden
        </button>

        {/* Legal links */}
        <div className="rounded-app overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          {[
            { icon: <Shield size={15} />, label: 'Datenschutz', href: '/datenschutz' },
            { icon: <Star size={15} />, label: 'AGB', href: '/agb' },
            { icon: <RotateCcw size={15} />, label: 'Widerrufsbelehrung', href: '/widerruf' },
            { icon: <Shield size={15} />, label: 'Impressum', href: '/impressum' },
          ].map((item, idx) => (
            <Link
              key={item.label}
              href={item.href}
              className="flex items-center gap-3 px-4 py-3.5 transition-colors duration-150"
              style={{ background: 'var(--card)', borderTop: idx > 0 ? '1px solid var(--border)' : 'none', color: 'var(--card-foreground)' }}>
              <span style={{ color: 'var(--muted-foreground)' }}>{item.icon}</span>
              <span className="flex-1 text-sm">{item.label}</span>
              <ChevronRight size={14} color="var(--muted-foreground)" />
            </Link>
          ))}
        </div>

        {/* Delete Account */}
        <button
          className="w-full py-3.5 rounded-app text-sm font-semibold transition-all duration-150 active:scale-[0.99] flex items-center justify-center gap-2"
          style={{ background: 'rgba(239,68,68,0.06)', color: 'var(--error)', border: '1px solid rgba(239,68,68,0.15)' }}
          onClick={() => setShowDeleteConfirm(true)}>
          <Trash2 size={15} />
          Konto löschen &amp; alle Daten entfernen
        </button>

        <div className="h-2" />
      </div>

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <AvatarPickerModal
          currentKey={profile.avatar_key ?? ''}
          onSelect={handleSelectAvatar}
          onClose={() => setShowAvatarPicker(false)}
        />
      )}

      {/* Cancel Subscription Confirm */}
      {showCancelConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.8)' }}
          onClick={() => { if (!cancellingSubscription) setShowCancelConfirm(false); }}>
          <div
            className="w-full max-w-sm rounded-app p-6 space-y-4"
            style={{ background: 'var(--card)', border: '1px solid rgba(239,68,68,0.3)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <XCircle size={20} color="var(--error)" />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ color: 'var(--card-foreground)' }}>Abo kündigen</h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Du behältst WINEWOOD+ bis zum Ende der Laufzeit.</p>
              </div>
            </div>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Dein Abo wird zum Ende der aktuellen Laufzeit nicht verlängert. Du kannst WINEWOOD+ bis dahin weiter nutzen.
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 py-2.5 rounded-app text-sm font-medium transition-all duration-150 active:scale-95"
                style={{ background: 'var(--muted)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancellingSubscription}>
                Behalten
              </button>
              <button
                className="flex-1 py-2.5 rounded-app text-sm font-semibold transition-all duration-150 active:scale-95 flex items-center justify-center gap-2"
                style={{ background: 'var(--error)', color: '#fff', opacity: cancellingSubscription ? 0.7 : 1 }}
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}>
                {cancellingSubscription ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                Kündigen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inquiry Modal */}
      {showInquiryModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => setShowInquiryModal(false)}>
          <div
            className="w-full max-w-mobile rounded-t-[24px] p-5 pb-10 space-y-4"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold" style={{ color: 'var(--card-foreground)' }}>Anfrage senden</h3>
              <button onClick={() => setShowInquiryModal(false)} className="p-1.5 rounded-full" style={{ background: 'var(--muted)' }}>
                <X size={16} color="var(--muted-foreground)" />
              </button>
            </div>
            <textarea
              value={inquiryMessage}
              onChange={(e) => setInquiryMessage(e.target.value)}
              rows={6}
              className="w-full px-3 py-2.5 rounded-app text-sm outline-none resize-none"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--card-foreground)' }}
            />
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Deine Nachricht wird an das WINEWOOD Team gesendet.
            </p>
            <button
              className="w-full py-3 rounded-app text-sm font-semibold transition-all duration-150 active:scale-95"
              style={{ background: 'var(--primary)', color: '#fff' }}
              onClick={() => {
                toast.success('Anfrage gesendet!', { description: 'Wir melden uns bald bei dir.', icon: '📨', duration: 3000 });
                setShowInquiryModal(false);
              }}>
              Anfrage senden
            </button>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.8)' }}
          onClick={() => { if (!isDeleting) { setShowDeleteConfirm(false); setDeleteConfirmText(''); } }}>
          <div
            className="w-full max-w-sm rounded-app p-6 space-y-4"
            style={{ background: 'var(--card)', border: '1px solid rgba(239,68,68,0.3)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <AlertTriangle size={20} color="var(--error)" />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ color: 'var(--card-foreground)' }}>Konto löschen</h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Diese Aktion kann nicht rückgängig gemacht werden.</p>
              </div>
            </div>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Alle deine Daten, Favoriten und dein Profil werden dauerhaft gelöscht. Gib <strong style={{ color: 'var(--card-foreground)' }}>LÖSCHEN</strong> ein um zu bestätigen.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="LÖSCHEN"
              className="w-full px-3 py-2.5 rounded-app text-sm outline-none"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--card-foreground)' }}
            />
            <div className="flex gap-3">
              <button
                className="flex-1 py-2.5 rounded-app text-sm font-medium transition-all duration-150 active:scale-95"
                style={{ background: 'var(--muted)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                disabled={isDeleting}>
                Abbrechen
              </button>
              <button
                className="flex-1 py-2.5 rounded-app text-sm font-semibold transition-all duration-150 active:scale-95 flex items-center justify-center gap-2"
                style={{
                  background: deleteConfirmText === 'LÖSCHEN' ? 'var(--error)' : 'rgba(239,68,68,0.3)',
                  color: '#fff',
                  opacity: isDeleting ? 0.7 : 1,
                }}
                onClick={handleDeleteAccount}
                disabled={isDeleting}>
                {isDeleting ? <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> : <Trash2 size={14} />}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      <WinewoodPlusModal isOpen={showPlusModal} onClose={() => setShowPlusModal(false)} />

      {redeemingPromo && (
        <PromoRedeemModal
          promoCode={redeemingPromo}
          onPartnerConfirm={handlePartnerConfirm}
          onClose={() => setRedeemingPromo(null)}
        />
      )}

      <BottomNav />
    </div>
  );
}

/* ─── Invoice Row ─── */
function InvoiceRow({ invoice, isLast }: { invoice: StripeInvoice; isLast: boolean }) {
  const statusConfig: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
    paid: { label: 'Bezahlt', color: 'var(--primary)', bg: 'rgba(124,179,66,0.12)', icon: <CheckCircle2 size={11} /> },
    open: { label: 'Offen', color: 'var(--gold)', bg: 'rgba(234,179,8,0.12)', icon: <Clock size={11} /> },
    void: { label: 'Storniert', color: 'var(--muted-foreground)', bg: 'var(--muted)', icon: <XCircle size={11} /> },
    uncollectible: { label: 'Nicht einziehbar', color: 'var(--error)', bg: 'rgba(239,68,68,0.12)', icon: <AlertCircle size={11} /> },
  };
  const status = statusConfig[invoice.status || 'open'] || statusConfig['open'];
  const amount = invoice.status === 'paid' ? invoice.amount_paid : invoice.amount_due;

  return (
    <div
      className="px-4 py-3 flex items-center gap-3"
      style={{ borderBottom: isLast ? 'none' : '1px solid var(--border)' }}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--muted)' }}>
        <FileText size={14} color="var(--muted-foreground)" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold truncate" style={{ color: 'var(--card-foreground)' }}>
          {invoice.number || `Rechnung`}
        </p>
        <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
          {new Date(invoice.created * 1000).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-xs font-bold" style={{ color: 'var(--card-foreground)' }}>
          {(amount / 100).toFixed(2)} {invoice.currency.toUpperCase()}
        </span>
        <span
          className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-pill"
          style={{ background: status.bg, color: status.color }}>
          {status.icon}
          {status.label}
        </span>
        {(invoice.invoice_pdf || invoice.hosted_invoice_url) && (
          <a
            href={invoice.invoice_pdf || invoice.hosted_invoice_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
            style={{ background: 'rgba(124,179,66,0.12)', border: '1px solid rgba(124,179,66,0.25)' }}
            aria-label="Rechnung herunterladen">
            <Download size={12} color="var(--primary)" />
          </a>
        )}
      </div>
    </div>
  );
}

/* ─── Promo Code Row ─── */
interface PromoCodeRowProps {
  promoCode: {
    id: string;
    offer_id: string;
    promo_code: string;
    promo_code_text?: string | null;
    redeemed: boolean;
    redeemed_at: string | null;
    saved_at: string;
    offer?: { title: string; city: string };
  };
  onOpenRedeem: (promoCode: SavedPromoCode) => void;
  onDelete: (id: string) => void;
}

function PromoCodeRow({ promoCode, onOpenRedeem, onDelete }: PromoCodeRowProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(promoCode.promo_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="px-4 py-3"
      style={{ opacity: promoCode.redeemed ? 0.6 : 1 }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {promoCode.offer && (
            <p className="text-xs font-semibold truncate" style={{ color: 'var(--card-foreground)' }}>
              {promoCode.offer.title}
            </p>
          )}
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            {promoCode.offer?.city} · Gespeichert {new Date(promoCode.saved_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })}
          </p>
          {promoCode.promo_code_text && (
            <div className="flex items-center gap-1 mt-1">
              <Tag size={10} color="#7CB342" />
              <span className="text-[10px] font-semibold" style={{ color: '#7CB342' }}>{promoCode.promo_code_text}</span>
            </div>
          )}
        </div>
        {promoCode.redeemed && (
          <span
            className="text-[10px] font-bold px-1.5 py-0.5 rounded-pill flex-shrink-0"
            style={{ background: 'rgba(124,179,66,0.12)', color: 'var(--primary)' }}
          >
            Eingelöst
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mt-2">
        {/* Code pill */}
        <div
          className="flex items-center gap-2 flex-1 px-3 py-2 rounded-app-sm"
          style={{ background: 'var(--muted)', border: '1px dashed var(--border)' }}
        >
          <Tag size={12} color="var(--primary)" />
          <span className="text-sm font-bold tracking-widest flex-1" style={{ color: 'var(--card-foreground)' }}>
            {promoCode.promo_code}
          </span>
          <button onClick={handleCopy} className="p-1 rounded" aria-label="Code kopieren">
            {copied ? <Check size={13} color="var(--success)" /> : <Copy size={13} color="var(--muted-foreground)" />}
          </button>
        </div>

        {/* Actions */}
        <Link
          href={`/offer-detail-page?id=${promoCode.offer_id}`}
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-150 active:scale-90"
          style={{ background: 'rgba(124,179,66,0.12)', border: '1px solid rgba(124,179,66,0.25)' }}
          aria-label="Angebot öffnen"
        >
          <ExternalLink size={13} color="var(--primary)" />
        </Link>

        {!promoCode.redeemed && (
          <button
            onClick={() => onOpenRedeem(promoCode as SavedPromoCode)}
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-150 active:scale-90"
            style={{ background: 'rgba(124,179,66,0.12)', border: '1px solid rgba(124,179,66,0.25)' }}
            aria-label="Als eingelöst markieren"
          >
            <Check size={13} color="var(--primary)" />
          </button>
        )}

        <button
          onClick={() => onDelete(promoCode.id)}
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-150 active:scale-90"
          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
          aria-label="Promo-Code entfernen"
        >
          <Trash2 size={13} color="#ef4444" />
        </button>
      </div>
    </div>
  );
}

/* ─── Toggle Switch ─── */
function ToggleSwitch({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className="relative w-10 h-5.5 rounded-pill transition-all duration-200 flex-shrink-0"
      style={{
        background: value ? 'var(--primary)' : 'var(--border)',
        width: '40px',
        height: '22px',
      }}
      aria-checked={value}
      role="switch">
      <span
        className="absolute top-0.5 rounded-full bg-white transition-all duration-200"
        style={{
          width: '18px',
          height: '18px',
          left: value ? '20px' : '2px',
        }}
      />
    </button>
  );
}

/* ─── Avatar Picker Modal ─── */
interface AvatarPickerModalProps {
  currentKey: string;
  onSelect: (key: string) => void;
  onClose: () => void;
}

function AvatarPickerModal({ currentKey, onSelect, onClose }: AvatarPickerModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}>
      <div
        className="w-full max-w-mobile rounded-t-[24px] p-6 pb-28 animate-slide-up"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold" style={{ color: 'var(--card-foreground)' }}>Avatar wählen</h3>
          <button onClick={onClose} className="p-1.5 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {AVATARS.map((avatar) => {
            const isSelected = avatar.key === currentKey;
            return (
              <button
                key={avatar.key}
                onClick={() => onSelect(avatar.key)}
                className="flex flex-col items-center gap-2 p-3 rounded-app transition-all duration-150 active:scale-95"
                style={{
                  background: isSelected ? 'rgba(124,179,66,0.15)' : 'var(--muted)',
                  border: isSelected ? '2px solid var(--primary)' : '2px solid transparent',
                }}
                aria-label={avatar.label}
                aria-pressed={isSelected}>
                <div className="w-16 h-16 rounded-full overflow-hidden" style={{ border: isSelected ? '2px solid var(--primary)' : '2px solid transparent' }}>
                  <Image
                    src={avatar.src}
                    alt={avatar.label}
                    width={64}
                    height={64}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-[10px] font-medium" style={{ color: isSelected ? 'var(--primary)' : 'var(--muted-foreground)' }}>
                  {avatar.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Not logged in state ─── */
function NotLoggedIn() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 pb-24" style={{ background: 'var(--background)' }}>
      <div className="text-center max-w-xs">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--muted)' }}>
          <User size={28} color="var(--muted-foreground)" />
        </div>
        <h2 className="text-lg font--semibold mb-2" style={{ color: 'var(--card-foreground)' }}>Nicht angemeldet</h2>
        <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
          Melde dich an, um dein Profil, Favoriten und WINEWOOD+ zu verwalten.
        </p>
        <Link
          href="/sign-up-login-screen"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-pill text-sm font-semibold transition-all duration-150 active:scale-95"
          style={{ background: 'var(--primary)', color: '#fff' }}>
          Anmelden
          <ChevronRight size={14} />
        </Link>
      </div>
      <BottomNav />
    </div>
  );
}

/* ─── Helper: base64 to Uint8Array for VAPID ─── */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}